/** El armazón: sesión, elección de portal, barra lateral y rutas. */
import { useCallback, useEffect, useState } from 'react'
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom'

import { ErrorDeApi, api } from './api'
import { Cargando, Error as Aviso, Vacio } from './componentes/piezas'
import { Salir } from './componentes/sesion'
import { ProveedorDelPortal, usePortal, type Aviso as AvisoDato } from './contexto'
import Armazon from './espacio/Armazon'
import PaginaPublica from './espacio/PaginaPublica'
import { ProveedorDelEspacio } from './espacio/contexto'
import Inicio from './espacio/Inicio'
import PaginaVista from './espacio/PaginaVista'
import Actividad from './paginas/Actividad'
import Entrar from './paginas/Entrar'
import Menciones from './paginas/Menciones'
import Equipo from './paginas/Equipo'
import Portada from './paginas/Portada'
import Proyecto from './paginas/Proyecto'
import Proyectos from './paginas/Proyectos'
import Solicitud from './paginas/Solicitud'
import Solicitudes from './paginas/Solicitudes'
import Tablero from './paginas/Tablero'
import { PAPEL } from './textos'
import type { QuienSoy, Servidor } from './tipos'

interface Sesion {
  id: string
  username: string
  display_name: string
}

function AppConSesion() {
  const [sesion, setSesion] = useState<Sesion | null | undefined>(undefined)
  const [avisos, setAvisos] = useState<AvisoDato[]>([])

  const avisar = useCallback((texto: string, tipo: AvisoDato['tipo'] = 'normal') => {
    const id = Date.now() + Math.random()
    setAvisos((previos) => [...previos, { id, texto, tipo }])
    setTimeout(() => setAvisos((previos) => previos.filter((a) => a.id !== id)), 5000)
  }, [])

  useEffect(() => {
    api
      .get<Sesion>('/auth/me')
      .then(setSesion)
      .catch(() => setSesion(null))
  }, [])

  if (sesion === undefined) return <div className="cargando">Comprobando la sesión…</div>
  if (sesion === null) return <Entrar />

  return (
    <>
      <Routes>
        <Route path="/" element={<ElegirPortal />} />
        <Route path="/:guildId/*" element={<Marco avisar={avisar} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <div className="avisos">
        {avisos.map((a) => (
          <div key={a.id} className={`aviso aviso--${a.tipo}`} role="status">
            {a.texto}
          </div>
        ))}
      </div>
    </>
  )
}

/** Si sólo hay un portal se entra directo: elegir entre una cosa no es elegir. */
function ElegirPortal() {
  const [servidores, setServidores] = useState<Servidor[] | null>(null)
  const [error, setError] = useState('')
  const navegar = useNavigate()

  useEffect(() => {
    api
      .get<Servidor[]>('/portal/servidores')
      .then((lista) => {
        setServidores(lista)
        if (lista.length === 1) navegar(`/${lista[0].id}`, { replace: true })
      })
      .catch((e: ErrorDeApi) => setError(e.message))
  }, [navegar])

  if (error) return <div className="contenido"><Aviso>{error}</Aviso></div>
  if (!servidores) return <div className="contenido"><Cargando /></div>

  if (servidores.length === 0) {
    return (
      <div className="contenido">
        <Vacio titulo="Todavía no tienes acceso a ningún portal">
          <p>
            Pídele a quien administre el servidor que te dé de alta. Ya has iniciado sesión, así
            que le saldrás en la lista.
          </p>
          <Salir />
        </Vacio>
      </div>
    )
  }

  return (
    <div className="contenido">
      <h1 style={{ marginBottom: 16 }}>¿A qué portal entras?</h1>
      <ul className="lista tarjeta">
        {servidores.map((s) => (
          <li key={s.id}>
            <a className="fila" href={`/${s.id}`}>
              <div className="fila__principal">
                <div className="fila__titulo">{s.nombre}</div>
                <div className="fila__meta">{PAPEL[s.papel]}</div>
              </div>
              <span aria-hidden="true">→</span>
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
}

function Marco({ avisar }: { avisar: (t: string, tipo?: AvisoDato['tipo']) => void }) {
  const { guildId = '' } = useParams()
  const [datos, setDatos] = useState<{ yo: QuienSoy; servidor: Servidor } | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    setDatos(null)
    setError('')
    Promise.all([
      api.get<QuienSoy>(`/guilds/${guildId}/portal/yo`),
      api.get<Servidor[]>('/portal/servidores'),
    ])
      .then(([yo, servidores]) => {
        const servidor = servidores.find((s) => s.id === guildId)
        if (!servidor) {
          setError('Ese portal no existe o no tienes acceso.')
          return
        }
        setDatos({ yo, servidor })
      })
      .catch((e: ErrorDeApi) => setError(e.message))
  }, [guildId])

  if (error) {
    return (
      <div className="contenido">
        <Aviso>{error}</Aviso>
        <a className="boton" href="/">Volver a la lista de portales</a>
      </div>
    )
  }
  if (!datos) return <div className="cargando">Abriendo el portal…</div>

  return (
    <ProveedorDelPortal servidor={datos.servidor} yo={datos.yo} avisar={avisar}>
      <ProveedorDelEspacio>
        <Armazon>
        <div className="principal">
          <Rutas />
        </div>
        </Armazon>
      </ProveedorDelEspacio>
    </ProveedorDelPortal>
  )
}

/** Las pantallas del portal. Quien sólo tiene acceso a solicitudes (el «solicitante») no ve las
 * demás: se le lleva siempre a las solicitudes. El servidor lo hace cumplir igualmente; esto
 * sólo evita llevarle a pantallas que le darían un error. */
function Rutas() {
  const { puedo, servidor } = usePortal()
  if (!puedo('ver_espacio')) {
    return (
      <Routes>
        <Route path="solicitudes" element={<Solicitudes />} />
        <Route path="solicitudes/:numero" element={<Solicitud />} />
        <Route path="menciones" element={<Menciones />} />
        <Route path="*" element={<Navigate to={`/${servidor.id}/solicitudes`} replace />} />
      </Routes>
    )
  }
  return (
    <Routes>
      <Route index element={<Portada />} />
      <Route path="solicitudes" element={<Solicitudes />} />
      <Route path="solicitudes/:numero" element={<Solicitud />} />
      <Route path="menciones" element={<Menciones />} />
      <Route path="tareas" element={<Tablero />} />
      <Route path="proyectos" element={<Proyectos />} />
      <Route path="proyectos/:numero" element={<Proyecto />} />
      <Route path="equipo" element={<Equipo />} />
      <Route path="actividad" element={<Actividad />} />
      <Route path="paginas" element={<Inicio />} />
      <Route path="paginas/:id" element={<PaginaVista />} />
      <Route
        path="*"
        element={
          <div className="contenido">
            <Vacio titulo="Esa página no existe" />
          </div>
        }
      />
    </Routes>
  )
}

/** Una página publicada se lee sin sesión: se decide antes de comprobar quién eres. */
export default function App() {
  const publica = /^\/p\/([A-Za-z0-9_-]{8,64})\/?$/.exec(window.location.pathname)
  if (publica) return <PaginaPublica token={publica[1]} />
  return <AppConSesion />
}
